import { Add as AddIcon, Check as CheckIcon, Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
	Box,
	Button,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	InputBase,
	List,
	ListItem,
	ListItemText,
	Paper,
	Stack,
	TextField,
	Typography,
	useTheme,
	useMediaQuery
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams as useUrlSearchParams } from 'react-router-dom';
import i18next from '../../../lang';
import SortableTable, { SortableTableColumn } from '../../../components/SortableTable';
import { adminStyles } from '../../../theme/adminStyles';
import MobileCardView, { MobileCardField } from '../../../components/MobileCardView';
import { Category } from '../Product/Interfaces';

interface CategoryProduct {
  id: number;
  title: string;
}

interface DeleteConfirmDialogProps {
  open: boolean;
  category: Category | null;
  relatedProducts: CategoryProduct[];
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  category,
  relatedProducts,
  onClose,
  onConfirm,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{i18next.t('product_category.confirm_delete')}</DialogTitle>
    <DialogContent>
      {relatedProducts.length > 0 ? (
        <>
          <Typography>
            {i18next.t('product_category.products_registered', {
              categoryName: category?.name
            })}
          </Typography>
          <List>
            {relatedProducts.map((product) => (
              <ListItem key={product.id}>
                <ListItemText primary={product.title} />
              </ListItem>
            ))}
          </List>
          <Typography>{i18next.t('product_category.confirm_delete_type')}</Typography>
        </>
      ) : (
        <Typography>{i18next.t('product_category.confirm_delete_this_type')}</Typography>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{i18next.t('common.cancel')}</Button>
      <Button onClick={onConfirm} color="error">{i18next.t('common.delete')}</Button>
    </DialogActions>
  </Dialog>
);

const ProductCategoryManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDetail, setNewCategoryDetail] = useState('');
  const [newCategoryExtra, setNewCategoryExtra] = useState('');
  // URL クエリと同期 (戻るボタンで検索状態を復元するため)
  const [urlParams, setUrlParams] = useUrlSearchParams();
  const [sortColumn, setSortColumn] = useState<string | null>(urlParams.get('sort') || null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    (urlParams.get('dir') === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc'
  );

  const syncToUrl = useCallback((sCol: string | null, sDir: 'asc' | 'desc') => {
    const next = new URLSearchParams();
    if (sCol) next.set('sort', sCol);
    if (sCol && sDir !== 'asc') next.set('dir', sDir);
    setUrlParams(next, { replace: true });
  }, [setUrlParams]);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editRows, setEditRows] = useState<Category[]>([]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<CategoryProduct[]>([]);

  useEffect(() => {
    fetchCategories();
  }, [sortColumn, sortDirection]);

  const fetchCategories = async () => {
    try {
      const params = new URLSearchParams();
      if (sortColumn) {
        params.append('sortColumn', sortColumn);
        params.append('sortDirection', sortDirection);
      }
      const response = await axios.get('/api/v1/admin/product-categories', { params });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to get category list：', error);
    }
  };

  useEffect(() => {
    setEditRows([...categories]);
  }, [categories]);

  const handleCancelEdit = () => {
    setEditRows([...categories]);
    setEditingId(null);
  };

  const handleRowChange = (updated: Category) => {
    setEditRows(prev => prev.map(r => r.id === updated.id ? updated : r));
  };

  const handleSaveEdit = (row: Category) => {
    setEditingId(null);
    handleUpdateCategory(row.id, row.name, row.detail || '', row.extra || '');
  };

  const handleSort = (columnKey: string) => {
    let newCol: string | null;
    let newDir: 'asc' | 'desc';
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        newCol = columnKey;
        newDir = 'desc';
      } else {
        newCol = null;
        newDir = 'asc';
      }
    } else {
      newCol = columnKey;
      newDir = 'asc';
    }
    setSortColumn(newCol);
    setSortDirection(newDir);
    syncToUrl(newCol, newDir);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post('/api/v1/admin/product-categories', {
        name: newCategoryName,
        detail: newCategoryDetail,
        extra: newCategoryExtra
      });
      setNewCategoryName('');
      setNewCategoryDetail('');
      setNewCategoryExtra('');
      fetchCategories();
    } catch (error) {
      console.error('カテゴリーの追加に失敗しました：', error);
    }
  };

  const handleUpdateCategory = async (id: number, newName: string, newDetail: string, newExtra: string) => {
    try {
      await axios.put(`/api/v1/admin/product-categories/${id}`, {
        name: newName,
        detail: newDetail,
        extra: newExtra
      });
      fetchCategories();
    } catch (error) {
      console.error('カテゴリーの更新に失敗しました：', error);
    }
  };

  const handleDeleteClick = async (category: Category) => {
    try {
      const response = await axios.get(`/api/v1/admin/product-categories/${category.id}/products`);
      setRelatedProducts(response.data);
      setSelectedCategory(category);
      setDeleteDialogOpen(true);
    } catch (error) {
      console.error('関連商品の照会に失敗しました：', error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategory) return;
    try {
      await axios.delete(`/api/v1/admin/product-categories/${selectedCategory.id}`);
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
      setRelatedProducts([]);
      fetchCategories();
    } catch (error) {
      console.error('カテゴリーの削除に失敗しました：', error);
    }
  };

  const columns = useMemo((): SortableTableColumn<Category>[] => [
    {
      key: 'id',
      label: 'ID',
      width: 70,
    },
    {
      key: 'name',
      label: i18next.t('product_category.type_name'),
      renderEditCell: (row, onChange) => (
        <InputBase
          fullWidth
          autoFocus
          value={row.name}
          onChange={(e) => onChange({ ...row, name: e.target.value })}
          sx={{ fontSize: '0.8rem' }}
        />
      ),
    },
    {
      key: 'detail',
      label: i18next.t('common.description'),
      renderCell: (row) => row.detail || '',
      renderEditCell: (row, onChange) => (
        <InputBase
          fullWidth
          value={row.detail || ''}
          onChange={(e) => onChange({ ...row, detail: e.target.value })}
          sx={{ fontSize: '0.8rem' }}
        />
      ),
    },
    {
      key: 'extra',
      label: i18next.t('product.additional_info'),
      renderCell: (row) => row.extra || '',
      renderEditCell: (row, onChange) => (
        <InputBase
          fullWidth
          value={row.extra || ''}
          onChange={(e) => onChange({ ...row, extra: e.target.value })}
          sx={{ fontSize: '0.8rem' }}
        />
      ),
    },
    {
      key: 'edit',
      label: i18next.t('common.edit'),
      width: 100,
      sortable: false,
      renderCell: (row) => {
        if (editingId === row.id) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Button size="small" startIcon={<CheckIcon sx={{ m: 0, p: 0 }} />}
                onClick={(e) => { e.stopPropagation(); handleSaveEdit(editRows.find(r => r.id === row.id) || row); }}
                sx={{ fontSize: '0.6rem', minWidth: 'unset', p: 0, m: 0, ml: 1, '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 } }}
              >
                {i18next.t('common.save')}
              </Button>
              <Button size="small" color="error" startIcon={<CloseIcon sx={{ m: 0, p: 0 }} />}
                onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                sx={{ fontSize: '0.7rem', minWidth: 'unset', p: 0, m: 0, mr: 1, '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 } }}
              >
                {i18next.t('common.cancel')}
              </Button>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Button size="small" disabled={editingId != null && editingId !== row.id}
              startIcon={<EditIcon sx={{ m: 0, p: 0 }} />}
              onClick={(e) => { e.stopPropagation(); setEditingId(row.id); }}
              sx={{ fontSize: '0.7rem', minWidth: 'unset', p: 0, m: 0, '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 } }}
            >
              {i18next.t('common.edit')}
            </Button>
          </Box>
        );
      },
    },
    {
      key: 'delete',
      label: i18next.t('common.delete'),
      width: 70,
      sortable: false,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Button size="small" color="error" startIcon={<DeleteIcon sx={{ m: 0, p: 0 }} />}
            onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }}
            sx={{ fontSize: '0.7rem', minWidth: 'unset', p: 0, m: 0, '& .MuiButton-startIcon': { display: 'flex', alignItems: 'center', margin: 0 } }}
          >
            {i18next.t('common.delete')}
          </Button>
        </Box>
      ),
    },
  ], [editingId, editRows, categories, i18next.language]);

  const mobileCardFields: MobileCardField<Category>[] = [
    { key: 'name', label: i18next.t('product_category.type_name'), primary: true },
    { key: 'detail', label: i18next.t('common.description') },
    { key: 'extra', label: i18next.t('product.additional_info') },
    {
      key: 'actions', label: '', hideLabel: true,
      render: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<EditIcon />}
            onClick={(e) => { e.stopPropagation(); /* inline editing not supported on mobile card */ }}>
            {i18next.t('common.edit')}
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
            onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }}>
            {i18next.t('common.delete')}
          </Button>
        </Stack>
      )
    }
  ];

  return (
    <Container maxWidth={false} sx={adminStyles.fullWidthPageContainer}>
      {/* 新規追加エリア */}
      <Paper sx={adminStyles.filterPaper}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label={i18next.t('product_category.new_type_name')}
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            required
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={i18next.t('common.description')}
            value={newCategoryDetail}
            onChange={(e) => setNewCategoryDetail(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label={i18next.t('product.additional_info')}
            value={newCategoryExtra}
            onChange={(e) => setNewCategoryExtra(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCategory}
            sx={{ ...adminStyles.searchButton, minWidth: '120px' }}
          >
            {i18next.t('common.add')}
          </Button>
        </Stack>
      </Paper>

      {/* カテゴリーリスト */}
      <Paper sx={adminStyles.tablePaper}>
        {isMobile ? (
          <MobileCardView
            data={categories}
            fields={mobileCardFields}
            keyExtractor={(row) => row.id}
          />
        ) : (
          <SortableTable
            columns={columns}
            rows={editRows}
            rowKey={(row) => row.id}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            editingRowId={editingId}
            onRowChange={handleRowChange}
            rowSx={(row) => editingId === row.id ? { bgcolor: '#fdd', '& .MuiTableCell-root': { bgcolor: '#fdd' } } : {}}
          />
        )}
      </Paper>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        category={selectedCategory}
        relatedProducts={relatedProducts}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedCategory(null);
          setRelatedProducts([]);
        }}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};

export default ProductCategoryManagement;